// Genera y persiste un device_id (UUID) resistente a ITP de Safari.
// Orden de prioridad:
// 1. localStorage (rápido, pero volátil en Safari privado >7 días)
// 2. IndexedDB (persistente en PWA instalada, sobrevive ITP)
// 3. Generar nuevo UUID y guardar en ambos
//
// Fase 1: sin heartbeat, sin pageTracker — solo la fundación del device_id.

const STORAGE_KEY = "dp_device_id";
const DB_NAME = "dp_tracking";
const DB_STORE = "device";
const DB_KEY = "device_id";

function generateUuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback RFC4122 v4 simplificado para navegadores sin crypto.randomUUID
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function readLocalStorage() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeLocalStorage(deviceId) {
  try {
    localStorage.setItem(STORAGE_KEY, deviceId);
  } catch {
    // localStorage no disponible (privado/bloqueado) — IndexedDB es el respaldo
  }
}

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB no disponible"));
      return;
    }
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(DB_STORE)) {
        db.createObjectStore(DB_STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function readIndexedDb() {
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(DB_STORE, "readonly");
      const store = tx.objectStore(DB_STORE);
      const req = store.get(DB_KEY);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

async function writeIndexedDb(deviceId) {
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(DB_STORE, "readwrite");
      const store = tx.objectStore(DB_STORE);
      const req = store.put(deviceId, DB_KEY);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch {
    // IndexedDB no disponible — no bloquea, localStorage ya tiene el valor
  }
}

/**
 * Obtiene el device_id persistido, generando uno nuevo si no existe.
 * Resuelve siempre con un string (UUID).
 */
export async function getDeviceId() {
  const fromLocalStorage = readLocalStorage();
  if (fromLocalStorage) {
    // Asegura que IndexedDB también lo tenga (puede haberse perdido)
    writeIndexedDb(fromLocalStorage);
    return fromLocalStorage;
  }

  const fromIndexedDb = await readIndexedDb();
  if (fromIndexedDb) {
    writeLocalStorage(fromIndexedDb);
    return fromIndexedDb;
  }

  const newDeviceId = generateUuid();
  writeLocalStorage(newDeviceId);
  await writeIndexedDb(newDeviceId);
  return newDeviceId;
}

/**
 * Detecta plataforma, navegador y modo de display del dispositivo actual.
 */
export function getDeviceInfo() {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";

  let platform = "unknown";
  if (/Windows/i.test(ua)) platform = "windows";
  else if (/Macintosh|Mac OS X/i.test(ua) && !/iPhone|iPad|iPod/i.test(ua)) platform = "macos";
  else if (/Android/i.test(ua)) platform = "android";
  else if (/iPhone|iPad|iPod/i.test(ua)) platform = "ios";
  else if (/Linux/i.test(ua)) platform = "linux";

  let browser = "unknown";
  if (/Edg\//i.test(ua)) browser = "edge";
  else if (/Chrome\//i.test(ua) && !/Edg\//i.test(ua)) browser = "chrome";
  else if (/Firefox\//i.test(ua)) browser = "firefox";
  else if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) browser = "safari";

  const isStandalone =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(display-mode: standalone)").matches;

  let displayMode = "browser";
  if (isStandalone) {
    displayMode = /iPhone|iPad|iPod|Android/i.test(ua) ? "pwa-mobile" : "pwa-desktop";
  }

  return { platform, browser, displayMode };
}
